import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../../components/SEOHelmet';

/**
 * Shared shell for all legal/policy pages: a consistent header, last-updated
 * line, readable prose column, and a footer nav between the policies.
 *
 * Company identity is centralised in the COMPANY constant so every page shows
 * the same entity details. Fill the [BRACKETED] values with SIVRA Inc's real
 * registered address before launch.
 */
export const COMPANY = {
  legalName: 'SIVRA Inc.',
  brand: '12and22',
  jurisdiction: 'State of New York, United States',
  // TODO(launch): replace with SIVRA Inc.'s real registered address.
  address: '[SIVRA INC. REGISTERED ADDRESS, New York, NY, USA]',
  legalEmail: 'legal@12and22.com',
  privacyEmail: 'privacy@12and22.com',
  supportEmail: 'support@12and22.com',
  site: 'https://12and22.com',
  // Effective date of this policy set. Update when the policies change.
  effective: 'August 2026',
};

const policyNav = [
  { name: 'Terms of Service', to: '/terms' },
  { name: 'Privacy Policy', to: '/privacy' },
  { name: 'Acceptable Use', to: '/aup' },
  { name: 'Refund Policy', to: '/refund' },
  { name: 'SLA', to: '/sla' },
  { name: 'Cookie Policy', to: '/cookies' },
];

interface LegalLayoutProps {
  title: string;
  description: string;
  slug: string;
  children: React.ReactNode;
}

const LegalLayout: React.FC<LegalLayoutProps> = ({ title, description, slug, children }) => {
  return (
    <div className="bg-white">
      <SEOHelmet title={`${title} | 12and22`} description={description} canonicalUrl={`https://12and22.com/${slug}`} />

      {/* Header band */}
      <div className="bg-primary-navy text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          <p className="mt-2 text-gray-300 text-sm">
            {COMPANY.legalName} · {COMPANY.brand} — Effective {COMPANY.effective}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="legal-prose text-gray-700 leading-relaxed space-y-5">
          {children}
        </div>

        <p className="mt-10 text-sm text-gray-500">
          Questions about this policy? Contact us at{' '}
          <a href={`mailto:${COMPANY.legalEmail}`} className="text-primary-navy underline">{COMPANY.legalEmail}</a>.
        </p>

        {/* Cross-links */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-3">Other policies</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
            {policyNav.filter(p => p.to !== `/${slug}`).map(p => (
              <Link key={p.to} to={p.to} className="text-primary-navy hover:underline">{p.name}</Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/** Small helpers so each policy reads cleanly. */
export const H2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-lg font-bold text-gray-900 pt-4">{children}</h2>
);
export const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[15px]">{children}</p>
);
export const UL: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-[15px]">{children}</ul>
);

export default LegalLayout;
